import React, { useState, useMemo, useEffect } from 'react'
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table'
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material'
import {
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material'
import { 
  getMaestraModelos,
  getTasaDescuento,
  updateTasaDescuento,
  getCostoAlmacenamiento,
  updateCostoAlmacenamiento,
  importarUtilidadPorMoto
} from '@auteco/services/motosSnopServices'
import * as XLSX from 'xlsx'

const InputsFinancierosContainer = () => {
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [inputsFinancierosData, setInputsFinancierosData] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Estados para inputs financieros
  const [tasaDescuento, setTasaDescuento] = useState(0)
  const [costoAlmacenamiento, setCostoAlmacenamiento] = useState(0)
  const [editingTasa, setEditingTasa] = useState(false)
  const [editingCosto, setEditingCosto] = useState(false)
  const [tempTasa, setTempTasa] = useState(0)
  const [tempCosto, setTempCosto] = useState(0)
  const [loadingTasa, setLoadingTasa] = useState(false)
  const [loadingCosto, setLoadingCosto] = useState(false)

  // Función para cargar inputs financieros
  const loadInputsFinancieros = async () => {
    try {
      const [tasaResponse, costoResponse] = await Promise.all([
        getTasaDescuento(),
        getCostoAlmacenamiento()
      ])
      console.log(tasaResponse)
      console.log(costoResponse)
      if (tasaResponse && tasaResponse.success[0].Tasa_descuento_anual) {
        setTasaDescuento(tasaResponse.success[0].Tasa_descuento_anual)
        setTempTasa(tasaResponse.success[0].Tasa_descuento_anual)
      }
      
      if (costoResponse && costoResponse.success[0].Costo_por_metro_cuadrado) {
        setCostoAlmacenamiento(costoResponse.success[0].Costo_por_metro_cuadrado)
        setTempCosto(costoResponse.success[0].Costo_por_metro_cuadrado)
      }
    } catch (error) {
      console.error('Error al cargar inputs financieros:', error)
      setAlertMessage('Error al cargar inputs financieros')
      setShowAlert(true)
    }
  }

  useEffect(() => {
    const getMaestraModelosfetch = async () => {
      try {
        setLoading(true)
        const response = await getMaestraModelos()
        const mappedData = response.success?.map((item, index) => ({
          id: index + 1,
          compania: item.compania || '-',
          marca: item.marca || '-',
          modelo: item.modelo_planeacion || '-',
          material: item.material || '-',
          textoMaterial: item.texto_material || '-',
          fob: item.fob || 0,
          moneda: item.moneda || 'USD',
          estado: item.Estado || '-',
          diasInventario: item.dias_inventario_seleccionado || 0,
          cargabilidad: item.cargabilidad || 0,
          origen: item.origen || '-',
          proveedor: item.proveedor || '-',
          segmento: item.segmento || '-',
          utilidadUnitaria: item.Utilidad || 0,
          costo: item.Costo || 0,
          fecha: item.Fecha_ingesta || '-',
        })) || []
        
        setInputsFinancierosData(mappedData)
        
        // Cargar inputs financieros
        await loadInputsFinancieros()
      } catch (error) {
        console.error('Error al obtener datos:', error)
        setAlertMessage('Error al cargar los datos')
        setShowAlert(true)
      } finally {
        setLoading(false)
      }
    }
    getMaestraModelosfetch()
  }, [])

  // Funciones para manejar edición de tasa de descuento
  const handleClickTasa = () => {
    if (!editingTasa) {
      setEditingTasa(true)
      setTempTasa(tasaDescuento)
    }
  }

  const handleSaveTasa = async () => {
    setLoadingTasa(true)
    try {
      const response = await updateTasaDescuento(tempTasa)
      if (response) {
        setTasaDescuento(tempTasa)
        setEditingTasa(false)
        setAlertMessage('Tasa de descuento actualizada exitosamente')
        setShowAlert(true)
        // Recargar datos
        await loadInputsFinancieros()
      } else {
        setAlertMessage('Error al actualizar la tasa de descuento')
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error:', error)
      setAlertMessage('Error al actualizar la tasa de descuento')
      setShowAlert(true)
    } finally {
      setLoadingTasa(false)
    }
  }

  const handleKeyDownTasa = (e) => {
    if (e.key === 'Enter') {
      handleSaveTasa()
    } else if (e.key === 'Escape') {
      setEditingTasa(false)
      setTempTasa(tasaDescuento)
    }
  }

  // Funciones para manejar edición de costo de almacenamiento
  const handleClickCosto = () => {
    if (!editingCosto) {
      setEditingCosto(true)
      setTempCosto(costoAlmacenamiento)
    }
  }

  const handleSaveCosto = async () => {
    setLoadingCosto(true)
    try {
      const response = await updateCostoAlmacenamiento(tempCosto)
      if (response) {
        setCostoAlmacenamiento(tempCosto)
        setEditingCosto(false)
        setAlertMessage('Costo de almacenamiento actualizado exitosamente')
        setShowAlert(true)
        // Recargar datos
        await loadInputsFinancieros()
      } else {
        setAlertMessage('Error al actualizar el costo de almacenamiento')
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error:', error)
      setAlertMessage('Error al actualizar el costo de almacenamiento')
      setShowAlert(true)
    } finally {
      setLoadingCosto(false)
    }
  }

  const handleKeyDownCosto = (e) => {
    if (e.key === 'Enter') {
      handleSaveCosto()
    } else if (e.key === 'Escape') {
      setEditingCosto(false)
      setTempCosto(costoAlmacenamiento)
    }
  }

  const inputsFinancierosColumns = useMemo(
    () => [
      {
        accessorKey: 'compania',
        header: 'Compañía',
        size: 120,
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#f5f5f5',
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
          },
        },
      },
      {
        accessorKey: 'marca',
        header: 'Marca',
        size: 100,
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#f5f5f5',
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
          },
        },
      },
      {
        accessorKey: 'modelo',
        header: 'Modelo',
        size: 150,
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#f5f5f5',
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
          },
        },
      },
      {
        accessorKey: 'utilidadUnitaria',
        header: 'Utilidad Unitaria ($)',
        size: 180,
        enableEditing: true,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#c5cae9',
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
          },
        },
        Cell: ({ cell }) => (
          <Typography variant="body2">
            ${cell.getValue().toLocaleString()}
          </Typography>
        ),
      },
      {
        accessorKey: 'costo',
        header: 'Costo ($)',
        size: 150,
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#f5f5f5',
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
          },
        },
        Cell: ({ cell }) => (
          <Typography variant="body2">
            ${Number(cell.getValue()).toLocaleString()}
          </Typography>
        ),
      },
      {
        accessorKey: 'fecha',
        header: 'Última modificación',
        size: 150,
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: '#f5f5f5', 
            fontSize: '14px !important',
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
          },
        },
      },
    ],
    []
  )

  const inputsFinancierosTable = useMaterialReactTable({
    columns: inputsFinancierosColumns,
    data: inputsFinancierosData,
    enablePagination: true,
    enableSorting: true,
    enableEditing: false,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    density: "spacious",
    state: {
      isLoading: loading,
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: '500px',
        overflowX: 'auto',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 0,
        border: '1px solid #ddd',
      },
    },
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
  })

  const handleExport = () => {
    try {
      // Obtener solo las columnas visibles de la tabla, excluyendo 'Última modificación'
      const visibleColumns = inputsFinancierosTable.getVisibleLeafColumns().filter(
        col => col.columnDef.header !== 'Última modificación'
      )

      // Crear los headers basados en las columnas visibles
      const headers = visibleColumns.map(column => {
        // Cambiar "Costo ($)" a solo "Costo" para la exportación
        if (column.columnDef.header === 'Costo ($)') {
          return 'Costo'
        }
        return column.columnDef.header
      })

      // Obtener solo las filas filtradas/visibles de la tabla
      const filteredRows = inputsFinancierosTable.getFilteredRowModel().rows

      // Crear las filas de datos basadas en las filas filtradas y columnas visibles
      const rows = filteredRows.map(row =>
        visibleColumns.map(column => {
          const cellValue = row.getValue(column.id)
          // Formatear valores especiales
          if (column.id === 'fob' && cellValue) {
            return `$${Number(cellValue).toLocaleString()}`
          }
          if (column.id === 'utilidadUnitaria') {
            return `$${Number(cellValue).toLocaleString()}`
          }
          if (column.id === 'costo') {
            return `$${Number(cellValue).toLocaleString()}`
          }
          return cellValue || '-'
        })
      )

      // Combinar headers y datos en un array para XLSX
      const aoa = [headers, ...rows]
      const worksheet = XLSX.utils.aoa_to_sheet(aoa)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inputs Financieros')
      XLSX.writeFile(workbook, `inputs_financieros_filtered_${new Date().toISOString().split('T')[0]}.xlsx`)

      setAlertMessage(`Archivo Excel descargado exitosamente (${filteredRows.length} filas, ${visibleColumns.length} columnas)`)
      setShowAlert(true)
    } catch (error) {
      console.error('Error al exportar:', error)
      setAlertMessage('Error al descargar el archivo')
      setShowAlert(true)
    }
  }

  const handleImport = async (event) => {
    const file = event.target.files[0]
    if (file) {
      try {
        const result = await importarUtilidadPorMoto(file)
        if (result.success) {
          setAlertMessage(result.message)
          setShowAlert(true)
          // Recargar los datos después de la importación exitosa
          const response = await getMaestraModelos()
          const mappedData = response.success?.map((item, index) => ({
            id: index + 1,
            compania: item.compania || '-',
            marca: item.marca || '-',
            modelo: item.modelo_planeacion || '-',
            material: item.material || '-',
            textoMaterial: item.texto_material || '-',
            fob: item.fob || 0,
            moneda: item.moneda || 'USD',
            estado: item.Estado || '-',
            diasInventario: item.dias_inventario_seleccionado || 0,
            cargabilidad: item.cargabilidad || 0,
            origen: item.origen || '-',
            proveedor: item.proveedor || '-',
            segmento: item.segmento || '-',
            utilidadUnitaria: item.Utilidad || 0,
            costo: item.Costo || 0,
            fecha: item.Fecha_ingesta || '-',
          })) || []
          setInputsFinancierosData(mappedData)
          // También recargar inputs financieros
          await loadInputsFinancieros()
        } else {
          setAlertMessage(result.message)
          setShowAlert(true)
        }
      } catch (error) {
        console.error('Error al importar archivo:', error)
        setAlertMessage('Error al procesar el archivo de importación')
        setShowAlert(true)
      }
      // Limpiar el input
      event.target.value = ''
    }
  }

  const handleCalcular = () => {
    setAlertMessage('Cálculo realizado exitosamente')
    setShowAlert(true)
  }

  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  return (
    <Box>
      {/* Header con botones y métricas financieras */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 3 
      }}>
        {/* Botones Exportar e Importar */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || inputsFinancierosData.length === 0}
            sx={{
              borderColor: '#4c75aa',
              color: '#4c75aa',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#4c75aa',
              },
            }}
          >
            Exportar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            component="label"
            sx={{
              borderColor: '#4c75aa',
              color: '#4c75aa',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#4c75aa',
              },
            }}
          >
            Importar
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleImport}
            />
          </Button>
        </Box>

        {/* Métricas financieras */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end',
          gap: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Tasa descuento anual
            </Typography>
            <Box 
              onClick={handleClickTasa}
              sx={{
                border: '1px solid #ddd',
                padding: '8px 12px',
                backgroundColor: editingTasa ? '#fff' : '#f9f9f9',
                minWidth: '120px',
                textAlign: 'center',
                color: '#e74c3c',
                fontWeight: 'bold',
                cursor: editingTasa ? 'text' : 'pointer',
                '&:hover': {
                  backgroundColor: editingTasa ? '#fff' : '#f0f0f0',
                },
              }}
            >
              {loadingTasa ? (
                <CircularProgress size={16} />
              ) : editingTasa ? (
                <TextField
                  type="number"
                  value={tempTasa}
                  onChange={(e) => setTempTasa(Number(e.target.value))}
                  onKeyDown={handleKeyDownTasa}
                  onBlur={handleSaveTasa}
                  autoFocus
                  size="small"
                  InputProps={{
                    style: { 
                      fontSize: '14px',
                      textAlign: 'center',
                    },
                  }}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />
              ) : (
                <Typography variant="body2">
                  {(tasaDescuento * 100).toFixed(2)}%
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Costo almacenamiento moto por m2
            </Typography>
            <Box 
              onClick={handleClickCosto}
              sx={{
                border: '1px solid #ddd',
                padding: '8px 12px',
                backgroundColor: editingCosto ? '#fff' : '#f9f9f9',
                minWidth: '120px',
                textAlign: 'center',
                color: '#e74c3c',
                fontWeight: 'bold',
                cursor: editingCosto ? 'text' : 'pointer',
                '&:hover': {
                  backgroundColor: editingCosto ? '#fff' : '#f0f0f0',
                },
              }}
            >
              {loadingCosto ? (
                <CircularProgress size={16} />
              ) : editingCosto ? (
                <TextField
                  type="number"
                  value={tempCosto}
                  onChange={(e) => setTempCosto(Number(e.target.value))}
                  onKeyDown={handleKeyDownCosto}
                  onBlur={handleSaveCosto}
                  autoFocus
                  size="small"
                  InputProps={{
                    style: { 
                      fontSize: '14px',
                      textAlign: 'center',
                    },
                  }}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />
              ) : (
                <Typography variant="body2">
                  {costoAlmacenamiento.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabla de Inputs Financieros */}
      <MaterialReactTable table={inputsFinancierosTable} />

      {/* Footer con paginación y botón calcular */}
      {/* <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mt: 2,
        borderTop: '1px solid #ddd',
        pt: 2
      }}>
        <Button
          variant="outlined"
          onClick={handleCalcular}
          disabled={loading}
          sx={{
            borderColor: '#4c75aa',
            color: '#4c75aa',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#4c75aa',
            },
          }}
        >
          Calcular
        </Button>
      </Box> */}

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default InputsFinancierosContainer 